import {Toaster} from "@findyourcanopy/canopy-ui";

import {http} from "http/index";
import qs from "utils/query";
import analytics from "services/analytics";
import {extractError} from "store/helpers";

import {formatJointScreening, formatPhoneValidation} from "./formatters";

export const sendInvite = async ({branchId, renterId}) =>
  http.post("/agent/invites", {branchId, renterId});

export const getIdVerificationDocuments = ({branchId, guarantor, renterId, verificationId}) =>
  http.get(
    `/agent/identity-verification/${verificationId}/renter/${renterId}/documents${qs.stringify({
      branchId,
      guarantor: !!guarantor,
    })}`,
  );

export const getIDnowVerificationArchiveLink = async ({
  branchId,
  guarantor,
  renterId,
  verificationId,
}) =>
  http.get(
    `/agent/identity-verification/${verificationId}/renter/${renterId}/documents/archive${qs.stringify(
      {
        branchId,
        guarantor: !!guarantor,
      },
    )}`,
  );

/**
 * Creates guarantor request.
 * `accessLevel` is one of the available referencing types (INSTANT, FULL, etc).
 * `addonTypes` is one if the available addons (IDV, RTR, etc).
 */
export const createGuarantorRequest = async ({accessLevel, addonTypes, branchId, renterId}) =>
  http.post("agent/guarantor-request", {
    accessLevel,
    addonTypes,
    branchId,
    renterId,
  });

export const getGuarantors = async ({renterId, branchId, guarantor}) =>
  http.get(
    `/agent/guarantors/outcome-or-count${qs.stringify(
      {branchId, userId: renterId, guarantor: !!guarantor},
      {skipNulls: true},
    )}`,
  );

export const getUserJointScreeningData = async ({branchId, renterId}) =>
  http
    .get(`/agent/joint-screening/by-user?branchId=${branchId}&userId=${renterId}`)
    .then(formatJointScreening);

export const sendReminder = async id => http.post(`agent/invites/${id}/send-reminder`, {});

export const cancelInvite = async id => http.delete(`agent/invites/${id}`);

export const markAsFavorite = async data => http.post("agent/rent-passport/favourite", data);

export const markAsUnFavorite = async data => http.post("agent/rent-passport/unfavourite", data);

export const getBranchScreeningConnectedBranches = async ({renterId}) =>
  http.get(`/agent/branch-connections/access-levels/${renterId}`);

export const createRightToRentRequest = async ({renterId, branchId, branchConnectionId}) =>
  http.post("/agent/right-to-rent-request", {
    renterId,
    branchId,
    branchConnectionId,
  });

export const createIdVerificationRequest = async ({renterId, branchId}) =>
  http.post("/agent/identity-verification-request/send", {
    renterId,
    branchId,
  });

export const createRightToRentComplete = async data =>
  http.post("/agent/right-to-rent-request/complete", data);

export const cancelRightToRentRequest = async ({renterId, branchId}) =>
  http.post("/agent/right-to-rent-request/cancel", {
    renterId,
    branchId,
  });

export const setRightToRentPermissionExpiry = async ({renterId, branchId, permissionExpiresAt}) =>
  http.post("/agent/right-to-rent-request/permission-expiry", {
    renterId,
    branchId,
    permissionExpiresAt,
  });

export const getRightToRentRequestHistory = async ({renterId, branchId, guarantor}) =>
  http.get(
    `/agent/right-to-rent-request/history${qs.stringify({renterId, branchId, guarantor: !!guarantor})}`,
  );

export const cancelIdVerificationRequest = async ({renterId, branchId}) =>
  http.post("/agent/identity-verification-request/cancel", {
    renterId,
    branchId,
  });

// TODO: Remove with old rp page
export const createPrimaryScreeningRequest = async ({renterId, branchId, isGuarantor}) =>
  http.post("/priority-reference-request", {renterId, branchId, isGuarantor});

export const createPrimaryScreeningRequestBranchConnection = async branchConnectionId =>
  http.post(`/priority-reference-request/branch-connection/${branchConnectionId}`);

export const getSectionNotes = async ({item, renterId, branchConnectionId}) =>
  http.get(
    `/agent/${item}/admin-notes/user/${renterId}${qs.stringify({branchConnectionId}, {skipNulls: true})}`,
  ); // NOTE: item is section name (income, rent, savings)

export const getPepsAndSanctionsNotes = async ({sectionId, branchConnectionId}) =>
  http.get(
    `/peps-sanctions/${sectionId}/notes${qs.stringify({branchConnectionId}, {skipNulls: true})}`,
  );

export const getSummaryNotesReq = async ({renterId, branchConnectionId}) =>
  http.get(`/summary-notes/${renterId}${qs.stringify({branchConnectionId}, {skipNulls: true})}`);

export const setMoveInDetails = async data =>
  http.post("/agent/branch-connections/edit-move-in-details", data);

export const getRentPreferencesSection = async ({renterId, branchId, guarantor}) =>
  http.get(
    `agent/rental-preferences/screening-state/${renterId}${qs.stringify({
      branchId,
      guarantor: !!guarantor,
    })}`,
  );

export const validateUserPhoneNumber = ({number}) =>
  http.get(`/agent/user/validate-phone/${qs.stringify({number})}`).then(formatPhoneValidation);

export const getUserPhoneHintsByEmail = ({email}) =>
  http.get(`/agent/user/phone-hints/${qs.stringify({email})}`).then(formatPhoneValidation);

export const getManualIncomeVerificationItem = ({incomeId, renterId, branchId, guarantor}) =>
  http.get(
    `/agent/income/${incomeId}/renter/${renterId}/verification-item/manual${qs.stringify({
      branchId: branchId || undefined,
      guarantor: !!guarantor,
    })}`,
  );

export const getManualSavingsVerificationItem = ({savingId, renterId, branchId, guarantor}) =>
  http.get(
    `/agent/savings/${savingId}/renter/${renterId}/verification-item/manual${qs.stringify({
      branchId: branchId || undefined,
      guarantor: !!guarantor,
    })}`,
  );

export const downloadAllIncomeDocuments = async ({incomeId, renterId}) => {
  try {
    const page = window.open("", "_self");

    const response = await http.get(
      `/agent/income/${incomeId}/renter/${renterId}/verification-item/manual/archive`,
    );

    page.location.href = response.downloadLink;
  } catch (error) {
    analytics.logErrorWithLevel("ToastOverlay", error);
    Toaster.toastConfig.showError({title: extractError(error.message)});
  }
};
export const downloadAllSavingsDocuments = async ({savingId, renterId}) => {
  try {
    const page = window.open("", "_self");

    const response = await http.get(
      `/agent/savings/${savingId}/renter/${renterId}/verification-item/manual/archive`,
    );

    page.location.href = response.downloadLink;
  } catch (error) {
    analytics.logErrorWithLevel("ToastOverlay", error);
    Toaster.toastConfig.showError({title: extractError(error.message)});
  }
};

export const getCategorizationData = async ({frequency, source}) =>
  http.get(`/content-manager/document-type${qs.stringify({frequency, source})}`);

export const getManualAddressVerificationItem = ({rentId}) =>
  http.get(`/agent/rent/${rentId}/verification-item/manual`);

export const downloadAllAddressVerificationDocuments = async ({rentId}) => {
  try {
    const response = await http.get(`/agent/rent/${rentId}/verification-item/manual/archive`);

    if (response?.downloadLink) {
      window.location.href = response.downloadLink;
    } else {
      Toaster.toastConfig.showError({title: "Download link not available"});
    }
  } catch (error) {
    analytics.logErrorWithLevel("ToastOverlay", error);
    Toaster.toastConfig.showError({title: extractError(error.message)});
  }
};

export const reconcileVouchsafeSessions = ({renterId, branchId, guarantor}) =>
  http.post(`/agent/identity-verification/users/${renterId}/reconcile`, {
    branchId,
    guarantor: !!guarantor,
  });
